import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { productsAPI, ASSET_BASE, fileUrl, productFinalizeAPI, uploadUnitRealPhotos } from '../services/api';
import { getBranchProductDisplayPrices, isProductPendingPricing, isProductSellable } from '../utils/branchProductPrice';

const fmt = (n) => '₹' + Number(n || 0).toLocaleString('en-IN');

/** Transfer-in IMEI: branch override or listing price hint */
function transferUnitPriceLabel(product, row) {
  if (!row?.transferIn?.fromBranchId) return '—';
  const list = Number(row.transferListPrice);
  const off = Number(row.transferOfferPrice);
  if (!Number.isNaN(list) && list > 0) {
    if (!Number.isNaN(off) && off > 0) return `${fmt(off)} / ${fmt(list)}`;
    return fmt(list);
  }
  return `Listing ${fmt(product?.price)}`;
}

const CATEGORIES = [
  { key: 'all', label: 'All', ico: 'A' },
  { key: 'mobile', label: 'Mobiles', ico: 'M' },
  { key: 'old-mobile', label: 'Old Mobiles', ico: 'O' },
  { key: 'tv', label: 'TVs & Appliances', ico: 'T' },
  { key: 'home theatre', label: 'Home Theatre', ico: 'H' },
  { key: 'laptop', label: 'Laptops & Computers', ico: 'L' },
  { key: 'accessor', label: 'Accessories', ico: 'C' },
  { key: 'other', label: 'Others', ico: '…' }
];

const UNIT_QC_LABELS = {
  display: 'Display',
  touch: 'Touch',
  rearCamera: 'Rear camera',
  frontCamera: 'Front camera',
  speaker: 'Speaker',
  microphone: 'Microphone',
  wifi: 'Wi‑Fi',
  bluetooth: 'Bluetooth',
  network: 'Network',
  charging: 'Charging',
  fingerprint: 'Fingerprint',
  faceUnlock: 'Face unlock',
  buttons: 'Buttons',
  backPanel: 'Back Panel',
  body: 'Body / Frame',
  simTray: 'SIM Tray',
  vibration: 'Vibration',
  batteryCheck: 'Battery (charge/hold)',
  others: 'Others',
  bodyPanel: 'Body / panel'
};

function humanizeQcKey(k) {
  if (UNIT_QC_LABELS[k]) return UNIT_QC_LABELS[k];
  return String(k)
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .replace(/^\s+/, '')
    .replace(/^./, (c) => c.toUpperCase());
}

function normalizeQcObject(raw) {
  if (raw == null) return null;
  if (typeof raw === 'string') {
    try {
      const p = JSON.parse(raw);
      return typeof p === 'object' && p && !Array.isArray(p) ? p : null;
    } catch {
      return null;
    }
  }
  if (typeof raw === 'object' && !Array.isArray(raw)) return raw;
  return null;
}

function formatQcValueNode(v) {
  if (v === true || v === 'working' || v === 'ok') {
    return <span className="text-success">OK</span>;
  }
  if (v === false || v === 'issue') {
    return <span className="text-warning">Issue</span>;
  }
  if (typeof v === 'string' && v.trim()) {
    return <span className="text-warning">{v}</span>;
  }
  if (typeof v === 'number' && !Number.isNaN(v)) {
    return <span className="text-body">{String(v)}</span>;
  }
  return <span className="text-muted">—</span>;
}

function matchesCategory(product, cat) {
  if (cat === 'all') return true;
  const c = (product.category || '').toLowerCase();
  if (cat === 'other') {
    return !CATEGORIES.slice(1, -1).some((x) => c.includes(x.key));
  }
  return c.includes(cat);
}

function qcObjectHasMeaningfulKeys(obj) {
  const o = normalizeQcObject(obj);
  if (!o) return false;
  return Object.keys(o).some((k) => {
    const v = o[k];
    if (v === undefined || v === null || v === '') return false;
    return true;
  });
}

/** Per-IMEI: pehle unitQcReport; warna listing specs.qcReport (us IMEI ke liye wahi snapshot dikhega) */
function getEffectiveUnitQc(row, product) {
  const unit = normalizeQcObject(row?.unitQcReport);
  if (qcObjectHasMeaningfulKeys(unit)) {
    return { qc: unit, source: 'unit' };
  }
  const listing = normalizeQcObject(product?.specs?.qcReport);
  if (qcObjectHasMeaningfulKeys(listing)) {
    return { qc: listing, source: 'listing' };
  }
  return null;
}

function sortedQcKeys(qc) {
  const o = normalizeQcObject(qc);
  if (!o) return [];
  return Object.keys(o).sort((a, b) => humanizeQcKey(a).localeCompare(humanizeQcKey(b)));
}

export default function Products() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [sort, setSort] = useState('latest');
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailProduct, setDetailProduct] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [transferEdits, setTransferEdits] = useState({});
  const [transferSaving, setTransferSaving] = useState({});
  const [qcViewer, setQcViewer] = useState(null);
  const [finalizeIdx, setFinalizeIdx] = useState(null);
  const [finalizeForm, setFinalizeForm] = useState({ price: '', offerPrice: '', imageUrl: '' });
  const [finalizePhotos, setFinalizePhotos] = useState([]);
  const [finalizeSaving, setFinalizeSaving] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const load = async (q) => {
    setLoading(true);
    try {
      const res = await productsAPI.list({ all: 1, search: q || undefined, limit: 500 });
      setProducts(res.data.products || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(() => load(search), 280);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    const highlight = searchParams.get('highlight');
    if (highlight) openDetail(null, highlight);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    const list = (products || []).filter((p) => matchesCategory(p, category));
    const sorted = [...list];
    if (sort === 'price-asc') {
      sorted.sort(
        (a, b) => getBranchProductDisplayPrices(a).salePrice - getBranchProductDisplayPrices(b).salePrice
      );
    }
    if (sort === 'price-desc') {
      sorted.sort(
        (a, b) => getBranchProductDisplayPrices(b).salePrice - getBranchProductDisplayPrices(a).salePrice
      );
    }
    if (sort === 'stock-desc') sorted.sort((a, b) => (b.stock || 0) - (a.stock || 0));
    if (sort === 'latest') sorted.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
    return sorted;
  }, [products, category, sort]);

  const openDetail = async (e, productId) => {
    if (e) e.stopPropagation();
    setDetailLoading(true);
    setDetailOpen(true);
    setDetailProduct(null);
    try {
      const res = await productsAPI.getById(productId);
      setDetailProduct(res.data.product || null);
    } catch (err) {
      alert(err.response?.data?.message || err.message || 'Load failed');
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    if (!detailProduct?.imeiTracking?.length) {
      setTransferEdits({});
      setTransferSaving({});
      return;
    }
    const next = {};
    detailProduct.imeiTracking.forEach((row, idx) => {
      if (row.transferIn?.fromBranchId && String(row.status || 'available') === 'available') {
        next[idx] = {
          list: row.transferListPrice > 0 ? String(row.transferListPrice) : '',
          offer: row.transferOfferPrice > 0 ? String(row.transferOfferPrice) : ''
        };
      }
    });
    setTransferEdits(next);
    setTransferSaving({});
  }, [detailProduct]);

  const saveTransferPrice = async (idx) => {
    if (!detailProduct?._id) return;
    const draft = transferEdits[idx] || { list: '', offer: '' };
    setTransferSaving((s) => ({ ...s, [idx]: true }));
    try {
      const body = {};
      body.transferListPrice = draft.list === '' ? null : Number(draft.list);
      body.transferOfferPrice = draft.offer === '' ? null : Number(draft.offer);
      if (
        (body.transferListPrice !== null && (Number.isNaN(body.transferListPrice) || body.transferListPrice < 0)) ||
        (body.transferOfferPrice !== null && (Number.isNaN(body.transferOfferPrice) || body.transferOfferPrice < 0))
      ) {
        alert('Valid selling / offer price enter karein');
        return;
      }
      const res = await productsAPI.patchTransferPrice(detailProduct._id, idx, body);
      const updated = res.data || {};
      const patchProduct = (prev) => {
        if (!prev?.imeiTracking) return prev;
        const imeiTracking = prev.imeiTracking.map((row, i) =>
          i === idx
            ? {
                ...row,
                transferListPrice: updated.transferListPrice,
                transferOfferPrice: updated.transferOfferPrice
              }
            : row
        );
        const next = { ...prev, imeiTracking };
        if (updated.price != null) next.price = updated.price;
        if (updated.offerPrice !== undefined) next.offerPrice = updated.offerPrice;
        return next;
      };
      setDetailProduct(patchProduct);
      setProducts((list) =>
        list.map((p) => (String(p._id) === String(detailProduct._id) ? patchProduct(p) : p))
      );
    } catch (err) {
      alert(err.response?.data?.message || err.message || 'Save failed');
    } finally {
      setTransferSaving((s) => ({ ...s, [idx]: false }));
    }
  };

  const openFinalize = (idx) => {
    if (!detailProduct) return;
    const bp = Number(detailProduct.buyingPrice) || 0;
    setFinalizeIdx(idx);
    setFinalizeForm({
      price: detailProduct.price > bp ? String(detailProduct.price) : '',
      offerPrice: detailProduct.offerPrice ? String(detailProduct.offerPrice) : '',
      imageUrl: detailProduct.imageUrl || ''
    });
    setFinalizePhotos([]);
  };

  const submitFinalize = async () => {
    if (!detailProduct?._id || finalizeIdx == null) return;
    const price = Number(finalizeForm.price);
    const offerRaw = finalizeForm.offerPrice;
    const offerPrice = offerRaw === '' ? undefined : Number(offerRaw);
    if (!Number.isFinite(price) || price <= 0) {
      alert('Sale price enter karein');
      return;
    }
    if (offerPrice !== undefined && (Number.isNaN(offerPrice) || offerPrice >= price)) {
      alert('Offer price sale price se kam honi chahiye');
      return;
    }
    setFinalizeSaving(true);
    try {
      const res = await productFinalizeAPI.finalize(detailProduct._id, {
        imeiIndex: finalizeIdx,
        price,
        offerPrice,
        imageUrl: finalizeForm.imageUrl.trim() || undefined
      });
      const updated = res.data?.product;
      if (updated) {
        setDetailProduct(updated);
        setProducts((list) => list.map((p) => (String(p._id) === String(updated._id) ? updated : p)));
      }
      let photoWarn = '';
      if (finalizePhotos.length > 0) {
        try {
          await uploadUnitRealPhotos(detailProduct._id, finalizeIdx, finalizePhotos);
        } catch (photoErr) {
          photoWarn =
            photoErr.response?.data?.message ||
            photoErr.message ||
            'Photos upload failed';
        }
      }
      setFinalizeIdx(null);
      if (photoWarn) {
        alert(`Sale price save ho gayi. Photos upload nahi hui: ${photoWarn}`);
      } else {
        alert('Product ab sale ke liye ready hai.');
      }
      load(search);
    } catch (err) {
      alert(err.response?.data?.message || err.message || 'Finalize failed');
    } finally {
      setFinalizeSaving(false);
    }
  };

  return (
    <div>
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
        <div>
          <h4 className="mb-0 fw-700">Products</h4>
          <div className="text-muted" style={{ fontSize: 13 }}>All SKUs in this branch</div>
        </div>
        <div className="d-flex gap-2 flex-wrap">
          <input
            className="form-control"
            style={{ minWidth: 240 }}
            placeholder="Search product, brand, category…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select className="form-select" style={{ minWidth: 170 }} value={sort} onChange={(e) => setSort(e.target.value)}>
            <option value="latest">Latest first</option>
            <option value="price-asc">Price: Low to High</option>
            <option value="price-desc">Price: High to Low</option>
            <option value="stock-desc">Most stock first</option>
          </select>
          <button type="button" className="btn-new-sale" onClick={() => navigate('/new-sale')}>
            + New Sale
          </button>
        </div>
      </div>

      <div className="category-quickbar mb-3">
        {CATEGORIES.map((c) => (
          <button
            key={c.key}
            type="button"
            className={`category-chip ${category === c.key ? 'active' : ''}`}
            onClick={() => setCategory(c.key)}
          >
            <span className="chip-ico">{c.ico}</span>
            {c.label}
          </button>
        ))}
      </div>

      {loading && products.length === 0 && (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" />
          <div className="text-muted mt-3">Loading products…</div>
        </div>
      )}

      <div className="product-grid">
        {filtered.map((p) => {
          const inStock = (p.stock || 0) > 0;
          const pending = isProductPendingPricing(p);
          const sellable = isProductSellable(p);
          const { price: displayPrice, offer } = getBranchProductDisplayPrices(p);
          const emiEligible = p.emiEligible !== false;
          return (
            <div
              key={p._id}
              role="button"
              tabIndex={0}
              className="product-card"
              onClick={() => openDetail(null, p._id)}
              onKeyDown={(ev) => {
                if (ev.key === 'Enter' || ev.key === ' ') openDetail(ev, p._id);
              }}
              style={{ cursor: 'pointer' }}
            >
              <div className="pc-badge-row">
                <span className="pc-badge premium">Branch</span>
                {pending && <span className="pc-badge" style={{ background: '#fef3c7', color: '#92400e' }}>Pricing pending</span>}
                {!pending && emiEligible && <span className="pc-badge emi">EMI</span>}
              </div>
              <div className="pc-image">
                {p.imageUrl ? (
                  <img
                    src={p.imageUrl.startsWith('http') ? p.imageUrl : `${ASSET_BASE}/${p.imageUrl.replace(/^\//, '')}`}
                    alt={p.name}
                    style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: 'inherit', background: '#f8f9fa' }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <span
                  style={{
                    display: p.imageUrl ? 'none' : 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    height: '100%'
                  }}
                >
                  {(p.category || 'P').charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="pc-body">
                <div className="pc-title">{p.name}</div>
                <div className="pc-sub">
                  {p.brand ? `${p.brand} · ` : ''}
                  {p.category || 'Product'}
                </div>
                <div className="pc-price">
                  {pending ? (
                    <span className="text-muted" style={{ fontSize: 14 }}>Buy: {fmt(p.buyingPrice)}</span>
                  ) : (
                    <>
                      {offer && displayPrice > offer ? <span className="strike">{fmt(displayPrice)}</span> : null}
                      {fmt(offer || displayPrice)}
                    </>
                  )}
                </div>
                {p.minSellingPrice ? (
                  <div className="text-muted" style={{ fontSize: 11 }}>
                    Min sale: {fmt(p.minSellingPrice)}
                  </div>
                ) : null}
                <div className="small text-muted mb-1" style={{ fontSize: 11 }}>
                  Tap card for units · QC · pics
                </div>
                <div className="pc-foot">
                  <span className={inStock ? 'in-stock' : 'text-muted'}>
                    {inStock ? `${p.stock} in stock` : 'Out of stock'}
                  </span>
                  {inStock && sellable ? (
                    <button
                      type="button"
                      className="btn btn-sm btn-primary"
                      style={{ borderRadius: 8, fontSize: 11, padding: '3px 10px', fontWeight: 600 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/new-sale?productId=${encodeURIComponent(p._id)}`);
                      }}
                    >
                      Sell
                    </button>
                  ) : pending ? (
                    <span className="text-warning" style={{ fontSize: 11, fontWeight: 600 }}>Set price</span>
                  ) : emiEligible ? (
                    <span className="emi-ready">EMI Ready</span>
                  ) : null}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {!loading && filtered.length === 0 && (
        <div className="empty-state">
          <div className="es-icon">—</div>
          <div className="es-title">No products match</div>
          <div>Try a different search or category.</div>
        </div>
      )}

      {detailOpen && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.45)', zIndex: 1050 }}>
          <div className="modal-dialog modal-fullscreen-sm-down modal-xl modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header py-2">
                <h5 className="modal-title mb-0">
                  {detailLoading ? 'Loading…' : detailProduct?.name || 'Product'}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setDetailOpen(false);
                    setDetailProduct(null);
                  }}
                  aria-label="Close"
                />
              </div>
              <div className="modal-body">
                {detailLoading && (
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary" />
                  </div>
                )}
                {!detailLoading && detailProduct && (
                  <>
                    <p className="text-muted small mb-2">
                      {detailProduct.brand ? `${detailProduct.brand} · ` : ''}
                      {detailProduct.category}
                      {detailProduct._id ? ` · ID ${detailProduct._id}` : ''}
                    </p>
                    {(detailProduct.imeiTracking || []).length === 0 ? (
                      <div className="alert alert-light border">Is product par abhi IMEI rows nahi hain.</div>
                    ) : (
                      <div className="table-responsive">
                        <table className="table table-sm align-middle">
                          <thead>
                            <tr>
                              <th>IMEI 1</th>
                              <th>IMEI 2</th>
                              <th>Status</th>
                              <th>Branch ₹</th>
                              <th>Real media</th>
                              <th>QC</th>
                              <th>Note</th>
                            </tr>
                          </thead>
                          <tbody>
                            {detailProduct.imeiTracking.map((row, idx) => (
                              <tr key={`${row.imei1}-${idx}`}>
                                <td>
                                  <code>{row.imei1}</code>
                                </td>
                                <td>{row.imei2 ? <code>{row.imei2}</code> : '—'}</td>
                                <td>
                                  <span className="badge bg-secondary">{row.status || '—'}</span>
                                  {row.readyForSale === false && String(row.status || 'available') === 'available' && (
                                    <span className="badge bg-warning text-dark ms-1">Pending</span>
                                  )}
                                </td>
                                <td className="small" style={{ minWidth: 200 }}>
                                  {row.transferIn?.fromBranchId &&
                                  String(row.status || 'available') === 'available' ? (
                                    <div className="d-flex flex-column gap-1">
                                      <div className="d-flex gap-1 align-items-center flex-wrap">
                                        <input
                                          type="number"
                                          min="0"
                                          className="form-control form-control-sm"
                                          style={{ width: 88 }}
                                          placeholder="Selling"
                                          value={transferEdits[idx]?.list ?? ''}
                                          onChange={(e) =>
                                            setTransferEdits((ed) => ({
                                              ...ed,
                                              [idx]: { ...(ed[idx] || { offer: '' }), list: e.target.value }
                                            }))
                                          }
                                        />
                                        <input
                                          type="number"
                                          min="0"
                                          className="form-control form-control-sm"
                                          style={{ width: 88 }}
                                          placeholder="Offer"
                                          value={transferEdits[idx]?.offer ?? ''}
                                          onChange={(e) =>
                                            setTransferEdits((ed) => ({
                                              ...ed,
                                              [idx]: { ...(ed[idx] || { list: '' }), offer: e.target.value }
                                            }))
                                          }
                                        />
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-primary py-0"
                                          disabled={!!transferSaving[idx]}
                                          onClick={() => saveTransferPrice(idx)}
                                        >
                                          {transferSaving[idx] ? '…' : 'Save'}
                                        </button>
                                      </div>
                                      <span className="text-muted" style={{ fontSize: 11 }}>
                                        {transferUnitPriceLabel(detailProduct, row)}
                                      </span>
                                    </div>
                                  ) : row.readyForSale === false &&
                                    String(row.status || 'available') === 'available' ? (
                                    <div className="d-flex flex-column gap-1">
                                      <span className="text-muted">Buy: {fmt(detailProduct.buyingPrice)}</span>
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-warning"
                                        onClick={() => openFinalize(idx)}
                                      >
                                        Set sale price
                                      </button>
                                    </div>
                                  ) : (
                                    transferUnitPriceLabel(detailProduct, row)
                                  )}
                                </td>
                                <td>
                                  {(row.unitRealPhotos || []).length === 0 &&
                                  !row.unitRealVideoUrl &&
                                  row.unitRealVideoStatus !== 'processing' &&
                                  row.unitRealVideoStatus !== 'failed' ? (
                                    <span className="text-muted">—</span>
                                  ) : (
                                    <div className="d-flex gap-1 flex-wrap">
                                      {(row.unitRealPhotos || []).map((u) => (
                                        <a key={u} href={fileUrl(u)} target="_blank" rel="noopener noreferrer">
                                          <img
                                            src={fileUrl(u)}
                                            alt=""
                                            style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 6 }}
                                          />
                                        </a>
                                      ))}
                                      {row.unitRealVideoUrl ? (
                                        <div style={{ width: 160, maxWidth: '100%' }}>
                                          <video
                                            src={fileUrl(row.unitRealVideoUrl)}
                                            controls
                                            muted
                                            playsInline
                                            preload="metadata"
                                            style={{
                                              width: '100%',
                                              maxHeight: 90,
                                              borderRadius: 6,
                                              backgroundColor: '#000'
                                            }}
                                          />
                                        </div>
                                      ) : null}
                                      {!row.unitRealVideoUrl && row.unitRealVideoStatus === 'processing' ? (
                                        <span className="badge bg-info text-dark">Video processing</span>
                                      ) : null}
                                      {!row.unitRealVideoUrl && row.unitRealVideoStatus === 'failed' ? (
                                        <span className="badge bg-danger">Video failed</span>
                                      ) : null}
                                    </div>
                                  )}
                                </td>
                                <td>
                                  {(() => {
                                    const eff = getEffectiveUnitQc(row, detailProduct);
                                    if (!eff) {
                                      return <span className="text-muted">—</span>;
                                    }
                                    return (
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-outline-primary py-0"
                                        onClick={() =>
                                          setQcViewer({
                                            title: detailProduct.name,
                                            imei: row.imei1,
                                            imei2: row.imei2 || '',
                                            qc: eff.qc,
                                            qcSource: eff.source
                                          })
                                        }
                                      >
                                        View
                                      </button>
                                    );
                                  })()}
                                </td>
                                <td className="small text-muted">
                                  {row.transferIn?.fromBranchId
                                    ? `Transfer ← ${row.transferIn.fromBranchId}`
                                    : ''}
                                  {row.soldTo?.customerName ? ` Sold: ${row.soldTo.customerName}` : ''}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                )}
              </div>
              <div className="modal-footer py-2">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setDetailOpen(false);
                    setDetailProduct(null);
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {finalizeIdx != null && detailProduct && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1070 }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header py-2">
                <h5 className="modal-title mb-0">Sale ke liye ready karein</h5>
                <button type="button" className="btn-close" onClick={() => setFinalizeIdx(null)} />
              </div>
              <div className="modal-body">
                <p className="text-muted small">
                  Buy price: {fmt(detailProduct.buyingPrice)} · IMEI:{' '}
                  <code>{detailProduct.imeiTracking?.[finalizeIdx]?.imei1}</code>
                </p>
                <div className="mb-3">
                  <label className="form-label">Sale price (₹) *</label>
                  <input
                    type="number"
                    className="form-control"
                    value={finalizeForm.price}
                    onChange={(e) => setFinalizeForm((f) => ({ ...f, price: e.target.value }))}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Offer price (optional)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={finalizeForm.offerPrice}
                    onChange={(e) => setFinalizeForm((f) => ({ ...f, offerPrice: e.target.value }))}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Product image URL (optional)</label>
                  <input
                    className="form-control"
                    placeholder="https://… or /uploads/…"
                    value={finalizeForm.imageUrl}
                    onChange={(e) => setFinalizeForm((f) => ({ ...f, imageUrl: e.target.value }))}
                  />
                </div>
                <div className="mb-2">
                  <label className="form-label">Real photos (optional, max 3)</label>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    className="form-control"
                    onChange={(e) => setFinalizePhotos(Array.from(e.target.files || []).slice(0, 3))}
                  />
                </div>
              </div>
              <div className="modal-footer py-2">
                <button type="button" className="btn btn-secondary" onClick={() => setFinalizeIdx(null)}>
                  Cancel
                </button>
                <button type="button" className="btn btn-success" disabled={finalizeSaving} onClick={submitFinalize}>
                  {finalizeSaving ? 'Saving…' : 'Ready for sale'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {qcViewer && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}>
          <div className="modal-dialog modal-fullscreen-sm-down modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header py-2">
                <div className="flex-grow-1 me-2">
                  <h5 className="modal-title mb-0">QC report</h5>
                  <div className="small text-muted mt-1">
                    {qcViewer.title}
                    <br />
                    <strong>IMEI 1:</strong> <code>{qcViewer.imei}</code>
                    {qcViewer.imei2 ? (
                      <>
                        <br />
                        <strong>IMEI 2:</strong> <code>{qcViewer.imei2}</code>
                      </>
                    ) : null}
                    <br />
                    <span className="badge bg-secondary mt-1">
                      {qcViewer.qcSource === 'unit' ? 'Is unit ka QC' : 'Listing / model QC (is IMEI par unit-QC nahi)'}
                    </span>
                  </div>
                </div>
                <button type="button" className="btn-close" onClick={() => setQcViewer(null)} />
              </div>
              <div className="modal-body small">
                {qcViewer.qcSource === 'listing' && (
                  <div className="alert alert-warning py-2 mb-2 small">
                    Ye <strong>product listing</strong> wala QC hai — jis IMEI par admin ne alag <strong>unit QC</strong> save kiya ho, wahan sirf wahi dikhega.
                  </div>
                )}
                <ul className="list-unstyled mb-0">
                  {sortedQcKeys(qcViewer.qc).length === 0 ? (
                    <li className="text-muted">Is QC snapshot mein abhi koi field save nahi mili.</li>
                  ) : (
                    sortedQcKeys(qcViewer.qc).map((k) => {
                      const v = qcViewer.qc[k];
                      return (
                        <li key={k} className="mb-2">
                          <strong>{humanizeQcKey(k)}:</strong> {formatQcValueNode(v)}
                        </li>
                      );
                    })
                  )}
                </ul>
              </div>
              <div className="modal-footer py-2">
                <button type="button" className="btn btn-secondary" onClick={() => setQcViewer(null)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
